import {CommonModule} from '@angular/common';
import {AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {Subject, of} from 'rxjs';
import {catchError, finalize, takeUntil} from 'rxjs/operators';

import {MaterialModule} from '../../../material.module';
import {Filter} from '../../../models/filter.model';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {ClientService} from '../../../services/auth/client.service';
import {FilterService} from '../../../services/enrich/filter.service';
import {ToastService} from '../../../services/layout/toast.service';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich';

type ImportFilterStatus = 'enabled' | 'disabled';

const SEARCH_QUERY_PARAM = 'search';
const STATUS_QUERY_PARAM = 'status';
const STATUS_VALUES: ImportFilterStatus[] = ['enabled', 'disabled'];

@Component({
  selector: 'app-enrich-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  host: {class: 'webinars-m3 webinars-shell'},
  standalone: true,
  imports: [CommonModule, MaterialModule, BreadcrumbsComponent]
})
export class EnrichFiltersComponent implements OnInit, AfterViewInit, OnDestroy {
  private filterService = inject(FilterService);
  private clientService = inject(ClientService);
  private clientLoadingService = inject(ClientLoadingService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);

  filters: Filter[] = [];
  readonly dataSource = new MatTableDataSource<Filter>([]);

  isLoading = false;
  error: string | null = null;
  filterTerm = '';
  statusFilter: ImportFilterStatus[] = [];

  displayedColumns: string[] = [
    'name',
    'description',
    'criteria',
    'enabled'
  ];

  numericFields: string[] = ['number_of_employees', 'latest_annual_revenue', 'latest_ebit', 'latest_sum_equity'];

  readonly breadcrumbs = [
    {label: 'Shine Enrich', route: '/enrich/setup'},
    {label: 'Company data', route: '/enrich/companies'},
    {label: 'Imports'}
  ];

  readonly statusFilters: Array<{value: ImportFilterStatus; label: string}> = [
    {value: 'enabled', label: 'Enabled'},
    {value: 'disabled', label: 'Disabled'}
  ];

  readonly statusMeta: Record<ImportFilterStatus, {label: string}> = {
    enabled: {label: 'Enabled'},
    disabled: {label: 'Disabled'}
  };

  private readonly destroy$ = new Subject<void>();
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.paginator.pageSize = 10;
    }
  }

  ngOnInit(): void {
    const legacyTab = this.route.snapshot.queryParamMap.get('tab');
    if (legacyTab === '2') {
      this.router.navigate(['/enrich/filters/new'], {queryParamsHandling: 'merge'});
      return;
    }

    this.hydrateFiltersFromQueryParams(this.route.snapshot.queryParamMap);

    const navigationState = this.router.getCurrentNavigation()?.extras.state as {successMessage?: unknown} | undefined;
    const historyState = history.state as {successMessage?: unknown} | undefined;
    const message = navigationState?.successMessage ?? historyState?.successMessage;
    if (typeof message === 'string' && message.trim().length) {
      this.toastService.showSuccess(message, 4000);
    }

    this.isLoading = true;
    this.error = null;

    this.clientLoadingService.ensureClientSelected().subscribe({
      next: () => {
        this.loadFilters();
      },
      error: err => {
        this.enrichDiagnostics.reportError('enrich.filters.client-selection', err);
        this.error = 'No client selected. Please select a client from the dropdown.';
        this.isLoading = false;
      }
    });

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(queryParamMap => {
      this.applyQueryParamState(queryParamMap);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRefreshClick(): void {
    this.filterTerm = '';
    this.statusFilter = [];
    this.syncQueryParams();
    this.loadFilters();
  }

  refresh(): void {
    this.onRefreshClick();
  }

  createNewFilter(): void {
    this.router.navigate(['/enrich/filters/new']);
  }

  editFilter(filter: Filter): void {
    this.router.navigate(['/enrich/filters', filter.id]);
  }

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.filterTerm = input?.value ?? '';
    this.applyFilters();
    this.syncQueryParams();
  }

  onStatusSelectChange(value: ImportFilterStatus[] | null | undefined): void {
    const incoming = (value ?? []) as ImportFilterStatus[];
    this.statusFilter = Array.from(new Set(incoming)) as ImportFilterStatus[];
    this.applyFilters();
    this.syncQueryParams();
  }

  clearFilters(): void {
    this.filterTerm = '';
    this.statusFilter = [];
    this.syncQueryParams();
    this.applyFilters();
  }

  get hasActiveFilters(): boolean {
    return this.filterTerm.trim().length > 0 || (this.statusFilter?.length ?? 0) > 0;
  }

  statusMetaFor(status: ImportFilterStatus): {label: string} {
    return this.statusMeta[status];
  }

  statusCountFor(value: ImportFilterStatus): number {
    switch (value) {
      case 'enabled':
        return this.filters.filter(filter => filter.enabled).length;
      case 'disabled':
        return this.filters.filter(filter => !filter.enabled).length;
    }

    return 0;
  }

  private parseStatusFromQueryParam(value: string | null): ImportFilterStatus[] {
    if (!value) {
      return [];
    }

    const parsed = value.split(',').map(token => token.trim().toLowerCase());
    const valid = parsed.filter(token => token === 'enabled' || token === 'disabled') as ImportFilterStatus[];
    const deduplicated = Array.from(new Set(valid));
    return deduplicated;
  }

  private normalizeStatusForQuery(statuses: ImportFilterStatus[]): string {
    const unique = Array.from(new Set(statuses));
    const sorted = STATUS_VALUES.filter(status => unique.includes(status));
    return sorted.join(',');
  }

  private applyQueryParamState(queryParamMap: ParamMap): void {
    const nextSearch = queryParamMap.get(SEARCH_QUERY_PARAM) ?? '';
    const nextStatus = this.parseStatusFromQueryParam(queryParamMap.get(STATUS_QUERY_PARAM));

    const searchChanged = this.filterTerm !== nextSearch;
    const statusChanged = JSON.stringify(this.statusFilter) !== JSON.stringify(nextStatus);

    if (searchChanged || statusChanged) {
      this.filterTerm = nextSearch;
      this.statusFilter = nextStatus;
      this.applyFilters();
    }
  }

  private hydrateFiltersFromQueryParams(queryParamMap: ParamMap): void {
    this.filterTerm = queryParamMap.get(SEARCH_QUERY_PARAM) ?? '';
    this.statusFilter = this.parseStatusFromQueryParam(queryParamMap.get(STATUS_QUERY_PARAM));
  }

  private syncQueryParams(): void {
    const statusValue = this.normalizeStatusForQuery(this.statusFilter);
    const rawSearch = this.filterTerm;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [SEARCH_QUERY_PARAM]: rawSearch.trim().length ? rawSearch : null,
        [STATUS_QUERY_PARAM]: statusValue ? statusValue : null
      },
      queryParamsHandling: 'merge'
    });
  }

  loadFilters(): void {
    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.error = 'No client selected';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.filterService
      .getFilters(clientId)
      .pipe(
        catchError(error => {
          this.enrichDiagnostics.reportError('enrich.filters.load', error);
          this.error = 'Failed to load filters';
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(filters => {
        this.filters = filters;
        this.applyFilters();
      });
  }

  applyFilters(): void {
    const term = this.filterTerm.trim().toLowerCase();
    const statusSet = new Set(this.statusFilter ?? []);

    const filtered = (this.filters ?? []).filter(filter => {
      if (statusSet.size) {
        if (filter.enabled && !statusSet.has('enabled')) {
          return false;
        }
        if (!filter.enabled && !statusSet.has('disabled')) {
          return false;
        }
      }

      if (!term) {
        return true;
      }

      const criteriaSummary = this.getFilterCriteriaSummary(filter);
      const haystack = `${filter.name ?? ''} ${filter.description ?? ''} ${criteriaSummary}`.toLowerCase();
      return haystack.includes(term);
    });

    this.dataSource.data = filtered;
    this.paginator?.firstPage();
  }

  // Helper method to format numeric comparison for display
  formatNumericComparison(field: string, value: any): string {
    if (!value) return '';

    const fieldLabel = this.getFieldLabel(field);

    if (typeof value === 'number') {
      return `${fieldLabel}: ${this.formatNumber(value)}`;
    }

    if (typeof value === 'object') {
      if (value.between) {
        return `${fieldLabel}: ${this.formatNumber(value.between[0])} - ${this.formatNumber(value.between[1])}`;
      } else if (value.gt !== undefined) {
        return `${fieldLabel}: > ${this.formatNumber(value.gt)}`;
      } else if (value.lt !== undefined) {
        return `${fieldLabel}: < ${this.formatNumber(value.lt)}`;
      } else if (value.gte !== undefined) {
        return `${fieldLabel}: ≥ ${this.formatNumber(value.gte)}`;
      } else if (value.lte !== undefined) {
        return `${fieldLabel}: ≤ ${this.formatNumber(value.lte)}`;
      }
    }

    return `${fieldLabel}: ${value}`;
  }

  // Helper method to get a human-readable field label
  getFieldLabel(field: string): string {
    const labels: {[key: string]: string} = {
      number_of_employees: 'Number of Employees',
      latest_annual_revenue: 'Annual Revenue',
      latest_ebit: 'EBIT',
      latest_sum_equity: 'Sum Equity',
      company_id: 'Company ID',
      city: 'City',
      postal_code: 'Postal Code',
      region: 'Region',
      municipality: 'Municipality',
      organization_type: 'Organization Type',
      accounting_year: 'Accounting Year',
      vat_registered: 'VAT Registered',
      in_company_register: 'In Company Register',
      in_foundation_register: 'In Foundation Register',
      in_voluntary_register: 'In Voluntary Register',
      in_party_register: 'In Party Register',
      nace_code1: 'NACE Code',
      number_of_employees_group: 'Employee Group'
    };

    return labels[field] || field;
  }

  // Helper method to format numbers according to locale
  formatNumber(value: number): string {
    return new Intl.NumberFormat().format(value);
  }

  // Method to get a readable summary of filter criteria
  getFilterCriteriaSummary(filter: Filter): string {
    if (!filter.criteria || Object.keys(filter.criteria).length === 0) {
      return 'No criteria defined';
    }

    const criteriaStrings: string[] = [];

    Object.keys(filter.criteria).forEach(key => {
      const value = filter.criteria[key];

      if (value === null || value === undefined || value === '') {
        return;
      }

      if (Array.isArray(value)) {
        if (value.length) criteriaStrings.push(`${this.getFieldLabel(key)}: (${value.join(' OR ')})`);
        return;
      }

      if (this.numericFields.includes(key)) {
        criteriaStrings.push(this.formatNumericComparison(key, value));
        return;
      }

      if (typeof value === 'boolean') {
        criteriaStrings.push(`${this.getFieldLabel(key)}: ${value ? 'Yes' : 'No'}`);
        return;
      }

      criteriaStrings.push(`${this.getFieldLabel(key)}: ${value}`);
    });

    return criteriaStrings.join(', ');
  }
}
